import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid, Tab, Tabs } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { GeneralSelectMenu } from 'src/components/general';
import GeneralVideoUploader from 'src/components/general/GeneralVideoUploader';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { UPLOAD_TO_SERVER_LIST } from 'src/constants/queryKeys';
import {
  networkCreateUploadToServer,
  networkUpdateUploadToServer,
  useFetchUploadToServerStatusList,
  useFetchUploadToServerTypeList,
} from 'src/network/server-management';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';
import useTabs from 'src/hooks/useTabs';
import { yupResolver } from '@hookform/resolvers/yup';
import { SCHEMA1, SCHEMA2 } from './validationSchema';
import * as Yup from 'yup';

const UPLOAD_TAB_MENU_ITEMS = [
  {
    value: 'UPLOAD_WITH_HARD_DISK',
    label: 'آپلود محتوا از روی هارد دیسک',
  },
  {
    value: 'UPLOAD_WITH_LINK',
    label: 'آپلود محتوا با لینک',
  },
];

const UploadToServerForm = () => {
  const { currentTab, onChangeTab } = useTabs('UPLOAD_WITH_HARD_DISK');

  const { modal, closeModal } = useFormsModalStore();

  const itemDetails = modal?.data;
  const isEditMode = !!itemDetails;

  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useQueryClient();

  const getFileExtension = (url) => {
    return url.split('.').pop().split(/\#|\?/)[0];
  };

  const [isCompleted, setIsCompleted] = useState(false);

  const [serverTypeList, setServerTypeList] = useState([]);
  const [selectedServerType, setSelectedServerType] = useState('');

  const [serverStatusList, setServerStatusList] = useState([]);
  const [selectedServerStatus, setSelectedServerStatus] = useState('');

  const defaultValues = getDefaultValues(itemDetails);

  const mainSchema = Yup.object().shape({
    ...SCHEMA1.fields,
    ...(currentTab === 'UPLOAD_WITH_LINK' && SCHEMA2.fields),
  });

  const methods = useForm({
    resolver: yupResolver(mainSchema), // todo schema
    defaultValues,
  });

  const {
    reset,
    trigger,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (values, event) => {
    const { UploadedFileName, ...PAYLOAD } = {
      // ...values,
      UploadToServerProfileID: null,
      UploadToServerServerDestinationURL: null,
      UploadToServerUploadToServerTypeID: ['rar', 'zip']?.includes(getFileExtension(values?.UploadToServerPathDisk)) ? 2 : 1,
      UploadToServerUploadToServerStatusID: 1,
      UploadToServerTitle: values?.UploadToServerTitle,
      UploadToServerDescription: values?.UploadToServerDescription,
      UploadedFileName: currentTab === 'UPLOAD_WITH_HARD_DISK' ? values?.UploadedFileNameWithHardDisk : '',
      UploadToServerPathDisk: currentTab === 'UPLOAD_WITH_HARD_DISK' ? values?.UploadToServerPathDisk : '/',
      UploadToServerPathHTTP: currentTab === 'UPLOAD_WITH_HARD_DISK' ? values?.UploadToServerPathHTTP : values?.UploadedFileNameWithLink,
    };

    try {
      if (isEditMode) {
        await networkUpdateUploadToServer(itemDetails?.UploadToServerID, PAYLOAD);
        reset();
        setIsCompleted(false);
        enqueueSnackbar('اطلاعات موردنظر با موفقیت ثبت شد');
        if (event.nativeEvent.submitter.name === 'save') {
          closeModal();
        }
        await queryClient.invalidateQueries({ queryKey: [UPLOAD_TO_SERVER_LIST] });
      } else {
        await networkCreateUploadToServer(PAYLOAD);
        reset();
        setIsCompleted(false);
        enqueueSnackbar('اطلاعات موردنظر با موفقیت ثبت شد');
        if (event.nativeEvent.submitter.name === 'save') {
          closeModal();
        }
        await queryClient.invalidateQueries({ queryKey: [UPLOAD_TO_SERVER_LIST] });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const { data: serverTypesResult } = useFetchUploadToServerTypeList();
  const { data: serverStatusResult } = useFetchUploadToServerStatusList();

  useEffect(() => {
    if (serverTypesResult) {
      const serverTypes = generateSelectMenuItems(serverTypesResult?.data?.items, 'UploadToServerTypeLable', 'UploadToServerTypeID');
      setServerTypeList(serverTypes);
      setSelectedServerType(serverTypes?.[0]?.id);
    }
  }, [serverTypesResult]);

  useEffect(() => {
    if (serverStatusResult) {
      const statusList = generateSelectMenuItems(serverStatusResult?.data?.items, 'UploadToServerStatusLable', 'UploadToServerStatusid');
      setServerStatusList(statusList);
      setSelectedServerStatus(statusList?.[0]?.id);
    }
  }, [serverStatusResult]);

  useEffect(() => {
    setSelectedServerStatus(itemDetails?.UploadToServerStatusid);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <div className="pr-7 w-full min-h-12">
            <Tabs allowScrollButtonsMobile scrollButtons={'auto'} value={currentTab} onChange={onChangeTab}>
              {UPLOAD_TAB_MENU_ITEMS.map((tab) => (
                <Tab disableRipple key={tab.value} value={tab.value} icon={tab.icon} label={tab.label} />
              ))}
            </Tabs>
          </div>

          <Box sx={{ p: 3 }}>
            <div style={{ display: currentTab === 'UPLOAD_WITH_HARD_DISK' ? 'block' : 'none' }}>
              <GeneralVideoUploader
                label="آپلود محتوا"
                id="uploaded_file_name"
                name="UploadedFileNameWithHardDisk"
                isCompleted={isCompleted}
                setIsCompleted={setIsCompleted}
                afterChangeAction={(res) => {
                  setValue('UploadedFileNameWithHardDisk', res?.file_name);
                  setValue('UploadToServerPathDisk', res?.file_name);
                  setValue('UploadToServerPathHTTP', res?.file_url);
                  trigger('UploadToServerPathDisk');
                  trigger('UploadToServerPathHTTP');
                  trigger('UploadedFileNameWithHardDisk');
                }}
              />
            </div>

            <div dir="ltr" style={{ display: currentTab === 'UPLOAD_WITH_LINK' ? 'block' : 'none' }}>
              <RHFTextField name="UploadedFileNameWithLink" type="text" label="لینک محتوا" />
            </div>

            <Box
              sx={{
                mt: 3,
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)' },
              }}
            >
              <RHFTextField name="UploadToServerTitle" type="text" label="عنوان" />
              {/* <RHFTextField name="UploadToServerServerDestinationURL" type="text" label="آدرس مقصد" /> */}

              <div className="hidden">
                <RHFTextField name="UploadToServerPathDisk" type="text" label="PathDisk" readOnly={true} />
              </div>

              <div className="hidden">
                <RHFTextField name="UploadToServerPathHTTP" type="text" label="PathHttp" readOnly={true} />
              </div>

              <div className="hidden">
                <GeneralSelectMenu
                  label="نوع سرور"
                  name="UploadToServerUploadToServerTypeID"
                  options={serverTypeList}
                  value={selectedServerType}
                  onChangeValue={setSelectedServerType}
                />
              </div>

              <div className="">
                <GeneralSelectMenu
                  label="وضعیت سرور"
                  name="UploadToServerUploadToServerStatusID"
                  options={serverStatusList}
                  value={selectedServerStatus}
                  onChangeValue={setSelectedServerStatus}
                />
              </div>
            </Box>

            <Box
              sx={{
                mt: 3,
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)' },
              }}
            >
              <RHFTextField name="UploadToServerDescription" multiline rows={6} label="توضیحات" />
            </Box>

            <div className="flex items-center gap-2 mt-6">
              {/* <div className={`flex items-center gap-2 mt-6 ${isCompleted ? 'flex' : 'hidden'}`}> */}
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                ذخیره و آپلود محتوای دیگر
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default UploadToServerForm;
